// ---------------------------------------------------------------------------
// MonthRangeGrid — grade ano x mes com click-and-drag para selecao de
// multiplos meses.
// ---------------------------------------------------------------------------
// Replicacao da UX do ``components/ResultsFilterBar/PeriodFilterModal.js``,
// adaptada para o modal "Enviar multiplos meses":
//
//   - Janela limitada a A-2..A+2 anos (5 linhas no maximo) — onde A e o
//     ano do mes em que o usuario estava quando abriu o modal. Sem
//     scroll vertical.
//   - So renderiza as colunas com pelo menos UM mes disponivel em
//     alguma linha da janela. Colunas sem dado naquela linha viram
//     placeholders vazios para manter o alinhamento.
//   - Click-and-drag: mousedown captura inicio + modificadores;
//     mouseenter atualiza overlay; mouseup global aplica selecao.
//   - Ctrl/Cmd: toggle individual (mantem selecao atual).
//   - Shift: estende selecao a partir do ultimo clique.
//   - Sem modificador: substitui selecao (range no drag, item unico no
//     click).
//   - Meses com arquivo ja entregue ganham um pontinho laranja no
//     canto superior direito (warning) — UI sinaliza "ao enviar de
//     novo, vai substituir" sem bloquear.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

const MONTH_ABBR_PT = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

/**
 * Constroi a chave canonica de um (year, month) — "YYYY-M" (mes sem
 * zero a esquerda, alinhado com o restante da V3). Centralizado num
 * helper pra evitar bugs de string ad-hoc.
 */
export function monthKey(year, month) {
  return `${year}-${month}`;
}

/**
 * Formata uma chave "YYYY-M" como "MMM/YY" (PT-BR). Usado nos
 * resumos textuais ("Jan/24 – Jun/24").
 */
export function formatMonthShort(key) {
  const [y, m] = String(key).split("-").map(Number);
  return `${MONTH_ABBR_PT[m - 1]}/${String(y).slice(-2)}`;
}

/**
 * MonthRangeGrid
 *
 * @param {object} props
 * @param {Array<{year: number, month: number}>} props.availableMonths
 *   Calendario disponivel da implementacao. A janela A-2..A+2 e
 *   aplicada por cima — qualquer mes fora dela e ignorado.
 * @param {number} props.anchorYear
 *   Ano "A" que centraliza a janela vertical. Normalmente o ``year``
 *   do mes selecionado no MonthSelectorV3 no momento de abrir o
 *   modal.
 * @param {Set<string>} props.selectedKeys
 *   Selecao corrente (chaves "YYYY-M").
 * @param {Set<string>} [props.deliveredKeys]
 *   Meses que ja tem arquivo entregue para o input. Recebem o
 *   marcador visual "vai substituir".
 * @param {(next: Set<string>) => void} props.onChange
 *   Callback chamado com a nova selecao apos o mouseup.
 * @param {object} props.classes
 *   Classes de JSS (vindas do useMultiMonthStyles do modal pai).
 */
function MonthRangeGrid({
  availableMonths,
  anchorYear,
  selectedKeys,
  deliveredKeys,
  onChange,
  classes,
}) {
  // ── Janela de anos e meses visiveis ─────────────────────────────────
  // A janela e [A-2, A+2]; dentro dela, exibimos so anos com dados.
  // O conjunto de meses (1..12) e fixo: garante alinhamento entre as
  // linhas mesmo quando uma linha tem buracos no calendario.
  const { orderedYears, monthsPerYear, allKeys } = useMemo(() => {
    const windowYears = new Set([
      anchorYear - 2,
      anchorYear - 1,
      anchorYear,
      anchorYear + 1,
      anchorYear + 2,
    ]);
    const byYear = new Map();
    for (const p of availableMonths || []) {
      if (!windowYears.has(p.year)) continue;
      if (!byYear.has(p.year)) byYear.set(p.year, new Set());
      byYear.get(p.year).add(p.month);
    }
    const years = [...byYear.keys()].sort((a, b) => a - b);
    const keys = new Set();
    years.forEach((y) =>
      [...byYear.get(y)].forEach((m) => keys.add(monthKey(y, m))),
    );
    return { orderedYears: years, monthsPerYear: byYear, allKeys: keys };
  }, [availableMonths, anchorYear]);

  // ── Lista ordenada de chaves para computo de ranges ────────────────
  // Drag de A..B = todas as chaves entre as duas posicoes nessa lista
  // (em ordem cronologica).
  const orderedKeys = useMemo(() => {
    const out = [];
    orderedYears.forEach((y) => {
      [...monthsPerYear.get(y)]
        .sort((a, b) => a - b)
        .forEach((m) => {
          out.push(monthKey(y, m));
        });
    });
    return out;
  }, [orderedYears, monthsPerYear]);
  const orderedKeysRef = useRef(orderedKeys);
  useEffect(() => {
    orderedKeysRef.current = orderedKeys;
  }, [orderedKeys]);

  // ── Estado de drag ──────────────────────────────────────────────────
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef(null);
  const dragEndRef = useRef(null);
  const dragModifiersRef = useRef({ ctrl: false, shift: false });
  const lastClickedRef = useRef(null);
  // Para repintar durante o drag sem disparar callback ao parent ate o
  // mouseup, mantemos um state ``dragOverlay`` (Set) local sem virar
  // setState aqui — recriamos via forceRerender quando o overlay muda.
  const [, forceRerender] = React.useReducer((x) => x + 1, 0);
  const dragOverlayRef = useRef(new Set());

  const computeRange = useCallback((a, b) => {
    const keys = orderedKeysRef.current;
    const ia = keys.indexOf(a);
    const ib = keys.indexOf(b);
    if (ia === -1 || ib === -1) return new Set();
    const [from, to] = ia <= ib ? [ia, ib] : [ib, ia];
    return new Set(keys.slice(from, to + 1));
  }, []);

  // ── Mouseup global ──────────────────────────────────────────────────
  useEffect(() => {
    const onUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      const start = dragStartRef.current;
      const end = dragEndRef.current;
      const { ctrl, shift } = dragModifiersRef.current;
      const isClick = start === end;

      let next;
      if (isClick) {
        if (ctrl) {
          next = new Set(selectedKeys);
          if (next.has(start)) next.delete(start);
          else next.add(start);
          lastClickedRef.current = start;
        } else if (shift && lastClickedRef.current) {
          next = new Set(selectedKeys);
          computeRange(lastClickedRef.current, start).forEach((k) =>
            next.add(k),
          );
        } else {
          next = new Set([start]);
          lastClickedRef.current = start;
        }
      } else {
        // drag → substitui pela faixa
        next = computeRange(start, end);
        lastClickedRef.current = null;
      }

      dragOverlayRef.current = new Set();
      dragStartRef.current = null;
      dragEndRef.current = null;
      forceRerender();
      onChange(next);
    };
    document.addEventListener("mouseup", onUp);
    return () => document.removeEventListener("mouseup", onUp);
  }, [computeRange, onChange, selectedKeys]);

  // ── Handlers das celulas ────────────────────────────────────────────
  const handleCellDown = useCallback(
    (key, e) => {
      if (!allKeys.has(key)) return;
      e.preventDefault();
      dragModifiersRef.current = {
        ctrl: e.ctrlKey || e.metaKey,
        shift: e.shiftKey,
      };
      dragStartRef.current = key;
      dragEndRef.current = key;
      dragOverlayRef.current = new Set([key]);
      isDraggingRef.current = true;
      forceRerender();
    },
    [allKeys],
  );

  const handleCellEnter = useCallback(
    (key) => {
      if (!isDraggingRef.current || !allKeys.has(key)) return;
      dragEndRef.current = key;
      dragOverlayRef.current = computeRange(dragStartRef.current, key);
      forceRerender();
    },
    [allKeys, computeRange],
  );

  // ── Render ──────────────────────────────────────────────────────────
  // Durante drag, exibe o overlay; senao, exibe a selecao "commitada".
  const highlighted = isDraggingRef.current
    ? dragOverlayRef.current
    : selectedKeys;

  // Todas as 12 colunas (Jan..Dez) — placeholders para meses ausentes
  // em determinada linha, garantindo alinhamento visual.
  const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  return (
    <div className={classes.monthGridArea}>
      <table className={classes.monthGrid}>
        <tbody>
          {orderedYears.map((year) => {
            const ms = monthsPerYear.get(year);
            return (
              <tr key={year}>
                <td className={classes.yearLabel}>{year}</td>
                {ALL_MONTHS.map((m) => {
                  if (!ms.has(m)) {
                    return <td key={m} aria-hidden="true" />;
                  }
                  const key = monthKey(year, m);
                  const isSelected = highlighted.has(key);
                  const isFilled = !!(deliveredKeys && deliveredKeys.has(key));
                  return (
                    <td key={m}>
                      <div
                        className={cx(classes.cell, {
                          [classes.cellSelected]: isSelected,
                          [classes.cellAlreadyFilled]: isFilled,
                        })}
                        onMouseDown={(e) => handleCellDown(key, e)}
                        onMouseEnter={() => handleCellEnter(key)}
                        title={
                          isFilled
                            ? `${MONTH_ABBR_PT[m - 1]} ${year} — já tem arquivo enviado`
                            : `${MONTH_ABBR_PT[m - 1]} ${year}`
                        }
                      >
                        {MONTH_ABBR_PT[m - 1]}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

MonthRangeGrid.propTypes = {
  availableMonths: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
    }),
  ).isRequired,
  anchorYear: PropTypes.number.isRequired,
  selectedKeys: PropTypes.instanceOf(Set).isRequired,
  deliveredKeys: PropTypes.instanceOf(Set),
  onChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired,
};

export default MonthRangeGrid;
